'use client'
import {
  createContext,
  type FC,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'

import {
  FEATURE_TOGGLE_SELLER_CENTRAL,
  useFeatureToggle,
} from '../../feature-toggles'
import {
  initialData,
  type MarketplacePartnerRegisterPageFormState,
  marketplacePartnerRegisterPageMakeFormState,
} from '../marketplacePartnerRegisterPageMakeFormState'

type MarketplacePartnerRegisterPageContextValue = {
  formRef: RefObject<HTMLFormElement | null>
  formState: MarketplacePartnerRegisterPageFormState
  isFormSubmitted: boolean | undefined
  isFormValid: boolean | undefined
  resetForm: () => void
  resetVersion: number
  setIsFormSubmitted: (isFormSubmitted: boolean) => void
  setIsFormValid: (isFormValid: boolean) => void
}

const MarketplacePartnerRegisterPageContext = createContext<
  MarketplacePartnerRegisterPageContextValue | undefined
>(undefined)

type MarketplacePartnerRegisterPageContextProviderProps = {
  children: ReactNode
}

export const MarketplacePartnerRegisterPageContextProvider: FC<
  MarketplacePartnerRegisterPageContextProviderProps
> = ({ children }) => {
  const intl = useIntl()
  const isSellerCentralFeatureEnabled = useFeatureToggle(
    FEATURE_TOGGLE_SELLER_CENTRAL,
  )
  const formState = useMemo(
    () =>
      marketplacePartnerRegisterPageMakeFormState({
        intl,
        isSellerCentralFeatureEnabled,
      }),
    [intl, isSellerCentralFeatureEnabled],
  )
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean | undefined>(
    undefined,
  )
  const [isFormValid, setIsFormValid] = useState<boolean | undefined>(undefined)
  const formRef = useRef<HTMLFormElement | null>(null)
  // form reset will be removed after introducing confirmation page (WSCR-3958)
  const [resetVersion, setResetVersion] = useState(0)

  const resetForm = useCallback(() => {
    Object.assign(formState.model, initialData)
    formState.model.validator.resetErrors()
    setResetVersion((version) => version + 1)
  }, [formState])

  const contextValue = useMemo(
    (): MarketplacePartnerRegisterPageContextValue => ({
      formRef,
      formState,
      isFormSubmitted,
      isFormValid,
      resetForm,
      resetVersion,
      setIsFormSubmitted,
      setIsFormValid,
    }),
    [
      formRef,
      formState,
      isFormSubmitted,
      isFormValid,
      setIsFormSubmitted,
      setIsFormValid,
      resetForm,
      resetVersion,
    ],
  )

  return (
    <MarketplacePartnerRegisterPageContext value={contextValue}>
      {children}
    </MarketplacePartnerRegisterPageContext>
  )
}

export const useMarketplacePartnerRegisterPageContext =
  (): MarketplacePartnerRegisterPageContextValue => {
    const contextValue = useContext(MarketplacePartnerRegisterPageContext)
    if (contextValue === undefined) {
      throw new Error(
        'useMarketplacePartnerRegisterPageContext must be used within a MarketplacePartnerRegisterPageContextProvider',
      )
    }

    return contextValue
  }
